import { hide, register, show } from "@ebay/nice-modal-react";
import {
  isAsyncThunkAction,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";
import { toast } from "react-hot-toast";

import type { AppStartListening } from "@/data/listenerMiddleware";
import {
  selectHasAutoSubmission,
  selectIsExam,
  selectIsTimedAssignment,
} from "@/features/assignment";
import {
  openSubmitPrompt,
  selectHasFinal,
  selectSubmitNetworkError,
  submit,
} from "@/features/authority";
import { SubmitStatusModal } from "@/features/submission";
import {
  examLateSubmissionDatePassed,
  writingDatePassed,
} from "@/features/timeline";
import { readWorkResults } from "@/graphql/selectors";
import { getCurrentDate } from "@/utils/datetime";
import { calculateDelayWithExponentialBackoffAndJitter } from "@/utils/jitter";

import { selectSubmitAttemptLimitReached } from "../selectors";

register("submit-status-modal", SubmitStatusModal);

/**
 * Start redux listeners related to the submission slice.
 */
export function startSubmitListeners(startListening: AppStartListening) {
  startTimedSubmissionListener(startListening);
  startSubmitActionListener(startListening);
}

/**
 * Listen for writing date to pass so that Auto Submissions or a Timed Submit
 * Modal can be displayed.
 */
function startTimedSubmissionListener(startListening: AppStartListening) {
  startListening({
    actionCreator: writingDatePassed,
    effect: async (_action, listenerApi) => {
      // Dismiss all toasts
      toast.dismiss();
      const state = listenerApi.getState();

      // Run background submit listeners only if the work is still being edited
      if (!isEditingWork(state.authority.workId)) {
        return;
      }

      // Submissions should take the writing date
      const submissionDate =
        state.timeline.writingDate.date ?? getCurrentDate().toISOString();

      const isExam = selectIsExam(state);
      const autoSubmission = selectHasAutoSubmission(state);
      const submitAttemptLimitReached = selectSubmitAttemptLimitReached(state);

      // If the submission attempt limit has been reached,
      // we should not attempt auto-submission or show the submit prompt
      // because the submission will definitely fail.
      if (submitAttemptLimitReached) {
        return;
      }

      // Exam auto submissions go through unconditionally — when time is up
      // we never block on the submission declaration. Explicit submits
      // (Submit button, Timed Submit Modal) still gate on declarations.
      if (isExam && autoSubmission) {
        await listenerApi.dispatch(
          submit({ submissionDate, autoSubmission: true })
        );
        return;
      }

      // Timed submit modal for an exam without auto submission.
      if (isExam && !autoSubmission) {
        listenerApi.dispatch(openSubmitPrompt(submissionDate));
        return;
      }

      // Timed submit modal for timed assignments.
      if (selectIsTimedAssignment(state)) {
        listenerApi.dispatch(openSubmitPrompt(submissionDate));
        return;
      }
    },
  });

  startListening({
    actionCreator: examLateSubmissionDatePassed,
    effect: async (_action, listenerApi) => {
      if (!selectHasFinal(listenerApi.getState())) {
        const now = getCurrentDate().toISOString();
        const submissionDate =
          listenerApi.getState().timeline.examLateSubmissionDate.date ?? now;
        listenerApi.dispatch(openSubmitPrompt(submissionDate));
      }
    },
  });
}

const isSubmitAction = isAsyncThunkAction(submit);

// Total number of submit request retries
export const MAX_ATTEMPTS = 10;

/**
 * Listen for the `submit` async action lifecycle.
 *
 * While the `submit` action is in in-flight, the Submit Status Modal tracks
 * it's loading state.
 *
 * On a submission error, the Submit Status Modal will display a error, while in
 * the background, we will retry the submission.
 */
function startSubmitActionListener(startListening: AppStartListening) {
  startListening({
    matcher: isSubmitAction,
    effect: async (action, listenerApi) => {
      const attempt = action.meta.arg?.attempt ?? 1;

      if (isPending(action)) {
        // Cancel a parked retry from a previous submission when a new one
        // starts. While a retry waits in `listenerApi.delay` below,
        // `submitLoading` is false, so the `condition` guard admits another
        // submission (e.g. the deadline auto-submission). If that succeeds, the
        // parked retry would wake and re-submit the already-submitted Work, and
        // the `rejected` after `fulfilled` flickers the page between editing and
        // submitted. Cancel only on `pending`: cancelling on `fulfilled`/
        // `rejected` could abort a newer submission's retry or modal work.
        listenerApi.cancelActiveListeners();

        const freshRetry = () => {
          // if the user retries to re-submit again, the number of attempts start from 1 again
          listenerApi.dispatch(submit({ ...action.meta.arg, attempt: 1 }));
        };
        return await show("submit-status-modal", {
          // Let user to retry submitting only if all the attempts on the background have failed
          onRetry: attempt > MAX_ATTEMPTS ? freshRetry : undefined,
          autoSubmission: action.meta.arg?.autoSubmission ?? false,
        });
      }

      // Hide the modal if everything is good.
      if (isFulfilled(action)) {
        return await hide("submit-status-modal");
      }

      // Failed submission network errors should be retried
      if (
        isRejected(action) &&
        selectSubmitNetworkError(listenerApi.getState()) !== null
      ) {
        // Retry the same request by copying the args. `navigator.onLine` is not
        // consulted: it is unreliable and must only drive messaging, not gate a
        // submission. Always send after the backoff; when offline the fetch
        // rejects quickly and re-enters this loop.
        const retrySubmitAfterDelay = async () => {
          // Artificially wait a bit inside the child
          const delayMs =
            calculateDelayWithExponentialBackoffAndJitter(attempt);
          await listenerApi.delay(delayMs);

          await listenerApi.dispatch(
            submit({
              ...action.meta.arg,
              attempt: attempt + 1,
            })
          );
        };

        if (attempt <= MAX_ATTEMPTS) {
          await retrySubmitAfterDelay();
        }
      }
    },
  });
}

// Determine if the current Work is being edited
function isEditingWork(workId: string): boolean {
  const workResult = readWorkResults(workId);
  if (!workResult || !workResult.finalResult) return true;

  const finalSubmission = workResult.finalResult.submission;
  if (!finalSubmission) return true;

  return finalSubmission.unsubmittedAt !== null;
}
